/**
 * Mirror of the C# ValidationRuleAppService.IsConditionMet/EvaluateCondition logic.
 * Used by the directive to decide whether a conditional rule applies, given the current
 * form/derived values.
 *
 * Supported operators (must match server):
 *   ==, !=, >=, <=, >, <
 *   true, false
 *   null, !null
 *   comma-separated lists: "1,2,3"  (membership check)
 *   AND combinations:  Field1 && Field2  with  Value1 && Value2
 *   OR  combinations:  Field1 || Field2  with  Value1 || Value2
 *   range on single field:  Field with "Value1 && Value2"  → both conditions must hold
 */

export function evaluateCondition(
    fieldExpr: string | null | undefined,
    valueExpr: string | null | undefined,
    context: Record<string, any>
): boolean {
    if (!fieldExpr || fieldExpr.trim() === '') return true;

    if (!valueExpr || valueExpr.trim() === '') {
        if (!fieldExpr.includes('&&') && !fieldExpr.includes('||')) {
            const val = context[fieldExpr.trim()];
            if (val == null) return false;
            const s = String(val);
            return s.trim() !== '';
        }
        return false;
    }

    if (fieldExpr.includes('||') || valueExpr.includes('||')) {
        const fields = fieldExpr.split('||').map(s => s.trim()).filter(Boolean);
        const values = valueExpr.split('||').map(s => s.trim()).filter(Boolean);
        if (fields.length === 1 && values.length > 1) {
            const val = context[fields[0]];
            return values.some(v => compareSingle(val, v));
        }
        if (fields.length !== values.length) return false;
        for (let i = 0; i < fields.length; i++) {
            if (compareSingle(context[fields[i]], values[i])) return true;
        }
        return false;
    }

    const fields = fieldExpr.split('&&').map(s => s.trim()).filter(Boolean);
    const values = valueExpr.split('&&').map(s => s.trim()).filter(Boolean);

    if (fields.length === 1 && values.length > 1) {
        const val = context[fields[0]];
        return values.every(v => compareSingle(val, v));
    }

    if (fields.length !== values.length) return false;

    for (let i = 0; i < fields.length; i++) {
        if (!compareSingle(context[fields[i]], values[i])) return false;
    }
    return true;
}

function compareSingle(value: any, condition: string): boolean {
    const valStr = value == null ? '' :
        typeof value === 'number' ? String(value) :
        typeof value === 'boolean' ? String(value) :
        String(value);

    // JSON array string (e.g. '["5","6"]' from multi-select stored as JSON)
    let arrayValue: any[] | null = null;
    if (Array.isArray(value)) {
        arrayValue = value;
    } else if (typeof valStr === 'string' && valStr.trim().startsWith('[') && valStr.trim().endsWith(']')) {
        try { arrayValue = JSON.parse(valStr); } catch {}
    }

    if (arrayValue && Array.isArray(arrayValue)) {
        const arr = arrayValue.map(v => String(v));
        if (condition.startsWith('contains:')) {
            return arr.some(v => v.toLowerCase() === condition.substring(9).trim().toLowerCase());
        }
        if (condition.startsWith('==')) {
            return arr.some(v => v.toLowerCase() === condition.substring(2).trim().toLowerCase());
        }
        if (condition.startsWith('!=')) {
            return !arr.some(v => v.toLowerCase() === condition.substring(2).trim().toLowerCase());
        }
        return arr.some(v => v.toLowerCase() === condition.toLowerCase());
    }

    if (condition.includes(',') && !condition.match(/^[<>=!]/)) {
        const allowed = condition.split(',').map(s => s.trim().toLowerCase());
        return allowed.includes(valStr.toLowerCase());
    }

    if (value instanceof Date) {
        if (condition.startsWith('>=')) {
            const d = new Date(condition.substring(2).trim());
            return !isNaN(d.getTime()) && value >= d;
        }
        if (condition.startsWith('<=')) {
            const d = new Date(condition.substring(2).trim());
            return !isNaN(d.getTime()) && value <= d;
        }
        if (condition.startsWith('>')) {
            const d = new Date(condition.substring(1).trim());
            return !isNaN(d.getTime()) && value > d;
        }
        if (condition.startsWith('<')) {
            const d = new Date(condition.substring(1).trim());
            return !isNaN(d.getTime()) && value < d;
        }
        if (condition.startsWith('==')) {
            const d = new Date(condition.substring(2).trim());
            return !isNaN(d.getTime()) && value.toDateString() === d.toDateString();
        }
        if (condition.startsWith('!=')) {
            const d = new Date(condition.substring(2).trim());
            return !isNaN(d.getTime()) && value.toDateString() !== d.toDateString();
        }
    }

    if (condition.startsWith('!=')) {
        return valStr.toLowerCase() !== condition.substring(2).trim().toLowerCase();
    }
    if (condition.startsWith('>=')) {
        const a = parseFloat(valStr); const b = parseFloat(condition.substring(2).trim());
        return !isNaN(a) && !isNaN(b) && a >= b;
    }
    if (condition.startsWith('<=')) {
        const a = parseFloat(valStr); const b = parseFloat(condition.substring(2).trim());
        return !isNaN(a) && !isNaN(b) && a <= b;
    }
    if (condition.startsWith('>')) {
        const a = parseFloat(valStr); const b = parseFloat(condition.substring(1).trim());
        return !isNaN(a) && !isNaN(b) && a > b;
    }
    if (condition.startsWith('<')) {
        const a = parseFloat(valStr); const b = parseFloat(condition.substring(1).trim());
        return !isNaN(a) && !isNaN(b) && a < b;
    }
    if (condition.startsWith('==')) {
        return valStr.toLowerCase() === condition.substring(2).trim().toLowerCase();
    }
    const cLower = condition.toLowerCase();
    if (cLower === 'null') return value == null || valStr.trim() === '';
    if (cLower === '!null') return value != null && valStr.trim() !== '';
    if (cLower === 'true') return value === true || valStr.toLowerCase() === 'true';
    if (cLower === 'false') return value === false || valStr.toLowerCase() === 'false';

    return valStr.toLowerCase() === condition.toLowerCase();
}
